/**
 * Dice Roller - panneau de des interactif avec tous les types de des JDR.
 * d4, d6, d8, d10, d12, d20, d100.
 * Resultat spectaculaire : particules, vibration, son, animation selon crit/echec.
 */

const DICE = [
  { sides: 4, label: 'd4', clip: 'polygon(50% 5%, 95% 90%, 5% 90%)' },
  { sides: 6, label: 'd6', clip: 'polygon(10% 10%, 90% 10%, 90% 90%, 10% 90%)' },
  { sides: 8, label: 'd8', clip: 'polygon(50% 2%, 98% 50%, 50% 98%, 2% 50%)' },
  { sides: 10, label: 'd10', clip: 'polygon(50% 0%, 97% 38%, 79% 98%, 21% 98%, 3% 38%)' },
  { sides: 12, label: 'd12', clip: 'polygon(50% 0%, 93% 25%, 93% 75%, 50% 100%, 7% 75%, 7% 25%)' },
  { sides: 20, label: 'd20', clip: 'polygon(50% 0%, 100% 36%, 82% 100%, 18% 100%, 0% 36%)' },
  { sides: 100, label: 'd%', clip: 'circle(48%)' },
];

const ICOSAHEDRON_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2">
  <polygon points="12,2 22,8.5 22,15.5 12,22 2,15.5 2,8.5" stroke-linejoin="round"/>
  <line x1="12" y1="2" x2="12" y2="22" opacity="0.4"/>
  <line x1="2" y1="8.5" x2="22" y2="15.5" opacity="0.4"/>
  <line x1="22" y1="8.5" x2="2" y2="15.5" opacity="0.4"/>
</svg>`;

function playCritSound() {
  const ctx = new AudioContext();
  const notes = [261.63, 329.63, 392.0, 523.25];
  notes.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.3, ctx.currentTime + i * 0.1);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + i * 0.1 + 0.15);
    osc.connect(gain).connect(ctx.destination);
    osc.start(ctx.currentTime + i * 0.1);
    osc.stop(ctx.currentTime + i * 0.1 + 0.15);
  });
  setTimeout(() => ctx.close(), 800);
}

function playFailSound() {
  const ctx = new AudioContext();
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(200, ctx.currentTime);
  osc.frequency.exponentialRampToValueAtTime(50, ctx.currentTime + 0.4);
  gain.gain.setValueAtTime(0.25, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
  osc.connect(gain).connect(ctx.destination);
  osc.start();
  osc.stop(ctx.currentTime + 0.4);
  setTimeout(() => ctx.close(), 600);
}

function createBurst(container: HTMLElement, x: number, y: number, isCrit: boolean) {
  const count = isCrit ? 30 : 12;
  const color = isCrit ? '227, 200, 122' : '201, 168, 76';
  for (let i = 0; i < count; i++) {
    const spark = document.createElement('div');
    spark.className = 'dice-spark';
    const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.5;
    const dist = 30 + Math.random() * (isCrit ? 80 : 40);
    spark.style.setProperty('--dx', `${Math.cos(angle) * dist}px`);
    spark.style.setProperty('--dy', `${Math.sin(angle) * dist}px`);
    spark.style.setProperty('--color', color);
    spark.style.left = `${x}px`;
    spark.style.top = `${y}px`;
    container.appendChild(spark);
    setTimeout(() => spark.remove(), 600);
  }
}

export function initD20(container: HTMLElement): () => void {

  const panel = document.createElement('div');
  panel.className = 'dice-panel';
  panel.innerHTML = `
    <button class="dice-toggle" title="Lancer les des" aria-label="Ouvrir le panneau de des">
      ${ICOSAHEDRON_SVG}
      <span class="dice-badge"></span>
    </button>
    <div class="dice-tray">
      <div class="dice-tray-grid">
        ${DICE.map(
          (d) => `
          <button class="dice-btn" data-sides="${d.sides}" title="Lancer un ${d.label}" style="clip-path: ${d.clip}">
            <span class="dice-btn-label">${d.label}</span>
          </button>
        `,
        ).join('')}
      </div>
      <div class="dice-result"></div>
      <div class="dice-history"></div>
    </div>
  `;
  container.appendChild(panel);

  const toggle = panel.querySelector('.dice-toggle') as HTMLElement;
  const badge = panel.querySelector('.dice-badge') as HTMLElement;
  const tray = panel.querySelector('.dice-tray') as HTMLElement;
  const resultEl = panel.querySelector('.dice-result') as HTMLElement;
  const historyEl = panel.querySelector('.dice-history') as HTMLElement;
  const history: string[] = [];
  let open = false;
  let lastResult = '';

  toggle.addEventListener('click', () => {
    open = !open;
    tray.classList.toggle('open', open);
    toggle.classList.toggle('active', open);
  });

  panel.querySelectorAll('.dice-btn').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      const sides = Number((btn as HTMLElement).dataset.sides);
      const result = 1 + Math.floor(Math.random() * sides);
      const isCrit = result === sides;
      const isFail = result === 1;

      // Vibration + screen effects
      if (navigator.vibrate) {
        if (isCrit) navigator.vibrate([50, 30, 50, 30, 100]);
        else if (isFail) navigator.vibrate([200, 100, 200, 100, 400]);
        else navigator.vibrate(30);
      }

      // Sound
      if (isCrit) playCritSound();
      if (isFail) playFailSound();

      if (isCrit) {
        document.body.classList.add('dice-glory-screen');
        setTimeout(() => document.body.classList.remove('dice-glory-screen'), 2500);
      }
      if (isFail) {
        document.body.classList.add('dice-doom-screen');
        setTimeout(() => document.body.classList.remove('dice-doom-screen'), 2000);
      }

      // Burst particles
      const rect = (btn as HTMLElement).getBoundingClientRect();
      const panelRect = panel.getBoundingClientRect();
      const x = rect.left - panelRect.left + rect.width / 2;
      const y = rect.top - panelRect.top + rect.height / 2;
      createBurst(panel, x, y, isCrit);

      // Result display
      resultEl.textContent = `${result}`;
      resultEl.className = 'dice-result dice-result-show';
      if (isCrit) resultEl.classList.add('dice-crit');
      if (isFail) resultEl.classList.add('dice-fail');

      setTimeout(() => {
        resultEl.classList.remove('dice-crit', 'dice-fail');
      }, 3000);

      // Persistent badge
      const label = sides === 100 ? 'd%' : `d${sides}`;
      lastResult = `${result}`;
      badge.textContent = lastResult;
      badge.classList.add('visible');

      // History
      history.unshift(`${label}:${result}`);
      if (history.length > 5) history.pop();
      historyEl.innerHTML = history.map((h) => `<span class="dice-hist-item">${h}</span>`).join('');

      // Button animation
      (btn as HTMLElement).classList.add('dice-btn-rolled');
      setTimeout(() => (btn as HTMLElement).classList.remove('dice-btn-rolled'), 400);

      e.stopPropagation();
    });
  });

  return () => panel.remove();
}
